import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const QRCode = ({ url }) => {
  if (!url) return null;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(url)}`;
  return <img src={qrCodeUrl} alt="QR Code for verification" />;
};

const CertificatePage = () => {
  const { serialNumber } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  const gramPradhanName = "श्रीमती प्रीति त्यागी";
  const signatureImgPath = "/pradhan_signature.png";

  useEffect(() => {
    const localApplications = JSON.parse(localStorage.getItem('applications')) || [];
    const foundApplication = localApplications.find(app => app.serialNumber === serialNumber);

    if (foundApplication && foundApplication.status === 'approved') {
      setApplication(foundApplication);
    } else {
      setApplication(null);
    }
    setLoading(false);
  }, [serialNumber]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="text-center py-10">Loading Certificate...</div>;
  }

  if (!application) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold text-red-600">Certificate Not Found or Not Approved</h2>
        <p className="mt-4 text-gray-700">This certificate cannot be generated. It may not have been approved yet, or the serial number is invalid.</p>
      </div>
    );
  }

  const verificationUrl = `${window.location.origin}/verify/${application.serialNumber}`;
  const issueDate = application.approvalDate 
    ? new Date(application.approvalDate).toLocaleDateString('en-GB') 
    : 'N/A';
  const dobFormatted = application.dob 
    ? new Date(application.dob).toLocaleDateString('en-GB')
    : 'N/A';

  const residenceStatus = application.residencePeriod === 'janm_se' 
    ? 'जन्म से' 
    : `दिनांक ${new Date(application.residenceDate).toLocaleDateString('en-GB')} से`;
    
  const casteInfo = application.caste && application.casteCategory 
    ? `इनकी जाति ${application.caste} है, जो ${application.casteCategory} वर्ग के अंतर्गत आती है।`
    : '';
  
  const incomeInfo = application.incomeSource ? `इनके परिवार की आय का मुख्य साधन ${application.incomeSource} है।` : '';

  return (
    <>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .certificate-print-area, .certificate-print-area * {
              visibility: visible;
            }
            .certificate-print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none;
            }
          }
        `}
      </style>
      <div className="bg-gray-100 min-h-screen py-10">
        <div className="w-full max-w-4xl mx-auto mb-6 flex justify-end px-4 sm:px-0 no-print">
          <button onClick={handlePrint} className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700">
            Print or Save as PDF
          </button>
        </div>

        <div className="certificate-container bg-white w-full max-w-4xl mx-auto p-6 shadow-lg certificate-print-area"> 
          <div className="border-4 border-double border-gray-800 p-5 relative">
            <div className="absolute inset-0 flex justify-center items-center z-0">
              <img src="/logo.png" alt="Logo Watermark" className="w-64 h-64 opacity-10" />
            </div>
            <div className="relative z-10">
              <div className="text-center mb-4">
                <h1 className="text-4xl font-bold text-gray-800">ग्राम पंचायत सिखेड़ा</h1>
                <p className="text-lg text-gray-600">तहसील - बुलंदशहर, जनपद - बुलंदशहर, उत्तर प्रदेश</p>
              </div>

              <div className="text-center my-4">
                  <div className="flex justify-center items-center gap-4 text-xl font-semibold">
                      <label className="flex items-center"><input type="checkbox" checked={application.certificateTypes.domicile} readOnly className="mr-2 h-5 w-5"/> निवास प्रमाण-पत्र</label>
                      <label className="flex items-center"><input type="checkbox" checked={application.certificateTypes.caste} readOnly className="mr-2 h-5 w-5"/> जाति प्रमाण-पत्र</label>
                      <label className="flex items-center"><input type="checkbox" checked={application.certificateTypes.income} readOnly className="mr-2 h-5 w-5"/> आय प्रमाण-पत्र</label>
                  </div>
              </div>

              <div className="flex justify-between items-start mt-6 mb-6">
                  <div className="text-lg leading-relaxed text-justify flex-grow">
                      <p className="indent-8">प्रमाणित किया जाता है कि <strong>{application.name_hi || application.name_en}</strong> पुत्र/पुत्री श्री <strong>{application.fatherName_hi || application.fatherName_en}</strong>, माता श्रीमती <strong>{application.motherName_hi || application.motherName_en}</strong>, जन्म तिथि <strong>{dobFormatted}</strong>, ग्राम व पोस्ट <strong>सिखेड़ा</strong>, तहसील <strong>बुलंदशहर</strong>, जनपद <strong>बुलंदशहर</strong> के निवासी हैं तथा मैं इनको भली भांति जानता हूँ।</p>
                      <p className="mt-4 indent-8">यह पंचायत में <strong>{residenceStatus}</strong> निवास करते हैं। {casteInfo} {casteInfo && incomeInfo ? ' ' : ''}{incomeInfo}</p>
                      <p className="mt-4 indent-8">मैं इनके उज्ज्वल भविष्य की कामना करता हूँ।</p>
                  </div>
                  <div className="ml-6 flex-shrink-0">
                      {application.photo && <img src={application.photo} alt="Applicant" className="w-32 h-40 object-cover border-2 border-gray-400 p-1"/>}
                  </div>
              </div>

              <div className="flex justify-between items-end mt-8">
                <div className="text-sm">
                  <p><strong>जारी करने की तिथि:</strong> {issueDate}</p>
                  <p><strong>सीरियल नंबर:</strong> {application.serialNumber}</p>
                  <div className="mt-2">
                    <QRCode url={verificationUrl} />
                  </div>
                </div>
                <div className="text-center">
                  <img src={signatureImgPath} alt="Digital Signature" className="h-20 w-auto mx-auto" />
                  <p className="border-t-2 border-gray-700 pt-1 mt-1 font-bold">{gramPradhanName}</p>
                  <p className="font-semibold">ग्राम प्रधान</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CertificatePage;
